import React from 'react';
import { Badge, Button, Card, Col, Dropdown, Form, Pagination, Row } from 'react-bootstrap';
import Tabela from '~/Components/TabelaDinamica/Tabela';
import { formatarData, formatarMoeda } from '../helpers/formatar';

const obterSequencia = (chave) => chave.sequencia || chave.Sequencia;

const TabelaResultados = ({
  chaves,
  total,
  totalizadores,
  paginacao,
  onPaginacaoChange,
  selecionadas,
  onToggleSelecionada,
  onSelecionarTodas,
  onAprovar,
  onEstornar,
  onCancelar,
  onVerificar,
  onDevolver,
  onAlterarVencimento,
  onAbrirOcorrencia,
  onAbrirSituacaoPagFor,
  obterCorLinha,
  carregando,
}) => {
  const totalPaginas = Math.max(1, Math.ceil((total || 0) / paginacao.pageSize));
  const paginaAtual = paginacao.pageNumber;
  const todosIds = chaves.map((c) => obterSequencia(c)).filter((seq) => seq != null);
  const todosSelecionados =
    todosIds.length > 0 && todosIds.every((seq) => selecionadas.has(seq));
  const totalSelecionado = chaves
    .filter((c) => selecionadas.has(obterSequencia(c)))
    .reduce((acc, c) => acc + Number(c.valor || 0), 0);
  const inicio = total === 0 ? 0 : (paginaAtual - 1) * paginacao.pageSize + 1;
  const fim = Math.min(total || 0, paginaAtual * paginacao.pageSize);

  const mudarPagina = (pageNumber) => {
    onPaginacaoChange({ ...paginacao, pageNumber });
  };

  const mudarOrdenacao = (ordem) => {
    onPaginacaoChange({
      ...paginacao,
      orderBy: ordem.coluna,
      orderDir: ordem.direcao === 'asc' ? 'ASC' : 'DESC',
      pageNumber: 1,
    });
  };

  const renderizarPaginacao = () => {
    const itens = [
      <Pagination.First
        key="first"
        disabled={paginaAtual === 1 || carregando}
        onClick={() => mudarPagina(1)}
      />,
      <Pagination.Prev
        key="prev"
        disabled={paginaAtual === 1 || carregando}
        onClick={() => mudarPagina(paginaAtual - 1)}
      />,
    ];
    const start = Math.max(1, paginaAtual - 2);
    const end = Math.min(totalPaginas, paginaAtual + 2);
    if (start > 1) itens.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
    for (let page = start; page <= end; page += 1) {
      itens.push(
        <Pagination.Item
          key={`page-${page}`}
          active={page === paginaAtual}
          disabled={carregando}
          onClick={() => mudarPagina(page)}
        >
          {page}
        </Pagination.Item>
      );
    }
    if (end < totalPaginas) itens.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
    itens.push(
      <Pagination.Next
        key="next"
        disabled={paginaAtual === totalPaginas || carregando}
        onClick={() => mudarPagina(paginaAtual + 1)}
      />,
      <Pagination.Last
        key="last"
        disabled={paginaAtual === totalPaginas || carregando}
        onClick={() => mudarPagina(totalPaginas)}
      />
    );
    return <Pagination className="mb-0">{itens}</Pagination>;
  };

  const colunas = [
    {
      key: 'selecionar',
      title: (
        <Form.Check
          id="autoriza-despesa-selecionar-todos"
          checked={todosSelecionados}
          onChange={() => onSelecionarTodas(todosSelecionados ? [] : todosIds)}
        />
      ),
      width: '48px',
      align: 'center',
      render: (value, item) => {
        const seq = obterSequencia(item);
        return (
          <Form.Check
            id={`autoriza-despesa-seq-${seq}`}
            checked={selecionadas.has(seq)}
            onChange={() => onToggleSelecionada(seq)}
            onClick={(e) => e.stopPropagation()}
          />
        );
      },
    },
    {
      key: 'cor_linha',
      title: '',
      width: '40px',
      align: 'center',
      render: (value, item) => (
        <span
          style={{
            display: 'inline-block',
            width: 18,
            height: 18,
            borderRadius: 3,
            border: '1px solid #adb5bd',
            backgroundColor: obterCorLinha(item),
          }}
        />
      ),
    },
    { key: 'sequencia', title: 'Chave', ordenacao: true, width: '90px' },
    { key: 'codigo_grupo_empresa', title: 'GE', ordenacao: true, width: '70px' },
    { key: 'codigo_conta', title: 'Conta', ordenacao: true, width: '90px' },
    { key: 'favorecido', title: 'Favorecido', ordenacao: true, width: '220px' },
    {
      key: 'data',
      title: 'Data',
      ordenacao: true,
      width: '110px',
      render: (value) => formatarData(value),
    },
    {
      key: 'vencimento',
      title: 'Vencimento',
      ordenacao: true,
      width: '120px',
      render: (value) => formatarData(value),
    },
    {
      key: 'valor',
      title: 'Valor',
      ordenacao: true,
      align: 'right',
      width: '120px',
      render: (value) => formatarMoeda(value),
    },
    { key: 'tp', title: 'TP', ordenacao: true, width: '70px' },
    {
      key: 'status',
      title: 'Status',
      ordenacao: true,
      width: '110px',
      render: (value) => <Badge variant={value === 'A' ? 'success' : 'warning'}>{value || '-'}</Badge>,
    },
    {
      key: 'valor_disponivel',
      title: 'ES',
      width: '70px',
      align: 'center',
      render: (value) => (
        <Badge variant={value === 'S' ? 'danger' : 'success'}>{value || '-'}</Badge>
      ),
    },
    {
      key: 'acoes_linha',
      title: 'Ações',
      width: '190px',
      align: 'center',
      render: (value, item) => {
        const seq = obterSequencia(item);
        return (
          <Dropdown onClick={(e) => e.stopPropagation()}>
            <Dropdown.Toggle size="sm" variant="outline-primary">
              Ações
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item onClick={() => onEstornar(seq)}>Estornar Aprovação</Dropdown.Item>
              <Dropdown.Item onClick={() => onAbrirSituacaoPagFor(seq)}>
                Situação Pagamento Fornecedor
              </Dropdown.Item>
              <Dropdown.Item onClick={() => onCancelar(seq)}>Cancelar Lançamento</Dropdown.Item>
              <Dropdown.Item onClick={() => onVerificar(seq)}>Verificar Pagamento</Dropdown.Item>
              <Dropdown.Item onClick={() => onDevolver(seq)}>Devolver ao Digitador</Dropdown.Item>
              <Dropdown.Item onClick={() => onAlterarVencimento(seq)}>
                Alterar Vencimento
              </Dropdown.Item>
              <Dropdown.Item onClick={() => onAbrirOcorrencia(seq)}>Cap_Ocorrência</Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        );
      },
    },
  ];

  const linhas = chaves.map((chave) => ({
    ...chave,
    id: obterSequencia(chave),
  }));

  return (
    <Card>
      <Card.Header>
        <Row className="align-items-center">
          <Col md={8}>
            <strong>Resultados</strong>
            <span className="ml-3">Selecionados: {selecionadas.size}</span>
            <span className="ml-3">Total selecionado: {formatarMoeda(totalSelecionado)}</span>
            <span className="ml-3">
              Total geral: {formatarMoeda(totalizadores && totalizadores.total_geral)}
            </span>
          </Col>
          <Col md={4} className="text-md-right mt-2 mt-md-0">
            <Button
              variant="success"
              onClick={onAprovar}
              disabled={carregando || selecionadas.size === 0}
            >
              Aprovar Selecionados
            </Button>
          </Col>
        </Row>
      </Card.Header>
      <Card.Body className="p-0">
        <div className="table-responsive">
          <Tabela
            colunas={colunas}
            dados={linhas}
            striped
            hover
            ordenacao={{
              coluna: paginacao.orderBy,
              direcao: paginacao.orderDir === 'ASC' ? 'asc' : 'desc',
            }}
            onOrdenacaoChange={mudarOrdenacao}
          />
        </div>
      </Card.Body>
      <Card.Footer>
        <Row className="align-items-center">
          <Col md={5} className="mb-2 mb-md-0">
            Mostrando {inicio} até {fim} de {total || 0} registros
          </Col>
          <Col md={7} className="d-flex justify-content-md-end">
            {renderizarPaginacao()}
          </Col>
        </Row>
      </Card.Footer>
    </Card>
  );
};

export default TabelaResultados;
